import { faChevronLeft, faChevronRight, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';

import './styles.scss';
import ConfirmResetModal from '../../../../components/confirm-reset-modal';
import { QuickPricerContext } from '../../../../providers/otc-trader/quick-pricer-provider';
import Utils from '../../../../utils/immersive_utils';
import { GROUP_TRADES, Trade, TradeParamsEnum, TRADES_PARAMS_LABELS } from '../../trade-models';
import TradeItem from '../trade-item';

interface GroupedTrades {
  [token: string]: Trade[];
}

const MODAL_RESET_TITLE = 'Are you sure you would like to reset the data?';
const MODAL_RESET_MESSAGE = 'All your line pricers will be deleted.';
const MODAL_CONFIRM_BUTTON = 'Reset anyway';

function sortByCreatedAt(arr: Trade[]) {
  return arr.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : -Infinity;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : -Infinity;
    return dateA - dateB;
  });
}

const groupTrades = (trades: Trade[]) => {
  const grouped = trades.reduce((acc: GroupedTrades, trade: Trade) => {
    const token = trade.isClear ? 'clear' : trade.token || 'clear';

    if (!acc[token]) {
      acc[token] = [];
    }
    acc[token].push(trade);

    return acc;
  }, {});

  for (const token in grouped) {
    grouped[token] = sortByCreatedAt(grouped[token]);
  }

  return grouped;
};

const SurfaceTrades = () => {
  const {
    trades,
    updateTrade,
    createTrade,
    resetTrade,
    duplicateTrade,
    deleteTrade,
    isTradeLoading,
    calculateTrade,
    getTradeData,
    expandableColumns,
    setExpandableColumns,
    tkPair,
  } = useContext(QuickPricerContext);

  const [openModal, setOpenModal] = useState(false);
  const [resetToken, setResetToken] = useState<string | undefined>(undefined);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
  }, [setOpenModal]);

  const handleOpenModal = useCallback(() => {
    setOpenModal(true);
  }, [setOpenModal]);

  const tokenPairToken = tkPair || '';

  const renderTradeRow = (trades: Trade[]) => {
    return trades.map((trade) => {
      return (
        <TradeItem
          key={trade.id}
          trade={trade}
          calculateTrade={calculateTrade}
          updateTrade={updateTrade}
          duplicateTrade={duplicateTrade}
          deleteTrade={deleteTrade}
          isLoading={isTradeLoading(trade.id)}
          getTradeData={getTradeData}
        />
      );
    });
  };

  const groupedTrades = groupTrades(trades);

  const hasChildren = (key: TradeParamsEnum) => {
    const group = GROUP_TRADES.find((g) => g.key === key);

    if (!group) return false;

    return group?.children?.length > 0;
  };

  const getParent = (key: TradeParamsEnum) => {
    return GROUP_TRADES.find((group) => group.children.includes(key));
  };

  const handleToggle = (key: TradeParamsEnum) => {
    setExpandableColumns((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  const renderHeader = (token: string) => {
    return (
      <div className="trades-header">
        {TRADES_PARAMS_LABELS.map((param) => {
          const isParent = hasChildren(param.key);
          const parent = getParent(param.key);
          const isExpanded = expandableColumns.includes(param.key);
          const isChild = !!parent;

          const canRenderChild = isChild && expandableColumns.includes(parent.key);

          const canRender = !isChild || canRenderChild;

          if (!canRender) return null;

          return (
            <div
              className="header-label"
              style={{ cursor: isParent ? 'pointer' : 'default' }}
              onClick={isParent ? () => handleToggle(param.key) : undefined}
              key={param.key}
            >
              <span>
                {param.key === TradeParamsEnum.instrument
                  ? `${token} ${param.label}${token ? 's' : ''}`
                  : param.label}
              </span>
              {isParent && (
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronLeft : faChevronRight}
                  className="icon"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTradeButtons = (token?: string) => {
    const handleResetButton = () => {
      setResetToken(token);

      handleOpenModal();
    };

    const setToken = !!tkPair;
    const isClear = !tkPair;

    return (
      <div className="trades-buttons">
        <Button onClick={() => createTrade(setToken, isClear, token)} className="btn-add-trade">
          <FontAwesomeIcon icon={faPlusCircle} className="icon" />
          <span>Add new trade</span>
        </Button>
        <Button onClick={handleResetButton} className="btn-restore-trades">
          <span>Reset to default</span>
        </Button>
      </div>
    );
  };

  const renderClearTrades = useMemo(() => {
    const clearTrades = groupedTrades['clear'] || [];

    if (tokenPairToken) return;

    if (clearTrades.length === 0) {
      createTrade();
    }

    return (
      <div className="surface-trades">
        {renderHeader('')}
        <div className="trades-rows">{renderTradeRow(clearTrades)}</div>
        {renderTradeButtons()}
      </div>
    );
  }, [groupedTrades, tkPair]);

  const renderTokenTrades = useMemo(() => {
    const tokens = Object.keys(groupedTrades).filter((item) => item !== 'clear');

    if (!!tokenPairToken && !tokens.length) {
      createTrade(true, false);
      return;
    }

    const sumTradeFields = (trades: Trade[]) => {
      const result: any = {};

      trades.forEach((trade) => {
        if (!trade.error) {
          (Object.keys(TradeParamsEnum) as Array<keyof typeof TradeParamsEnum>).forEach((key) => {
            const tradeValue = trade[key]?.toString() || '';
            let format = '';
            let decimalPoints = 2;
            const value = tradeValue;

            const sumTrades: string[] = [
              TradeParamsEnum.pv_acc,
              TradeParamsEnum.pv_und,
              TradeParamsEnum.delta,
              TradeParamsEnum.gamma,
              TradeParamsEnum.theta,
              TradeParamsEnum.vega,
            ];

            if (!sumTrades.includes(key)) {
              result[key] = '';
              return;
            }

            const spplitArr = tradeValue?.split(' ');

            if (spplitArr?.length === 2) {
              format = spplitArr[1];
              const value = spplitArr[0];

              if (value.split('.').length === 2) {
                decimalPoints = value.split('.')[1].length;
              }
            }

            const sanitalizedValue = `${value}`?.replace(/[a-zA-Z\s,]/g, '');
            const sanitalizedSavedValue = parseFloat(`${result[key]}`?.replace(/[a-zA-Z\s,]/g, ''));

            if (sanitalizedValue) {
              const numericValue = parseFloat(sanitalizedValue);
              if (!isNaN(numericValue)) {
                const formmated = `${Utils.formatNumber((sanitalizedSavedValue || 0) + numericValue, decimalPoints)} ${format}`;

                result[key] = formmated;
              }
            }
          });
        }
      });

      return result;
    };

    return tokens.map((token) => {
      const tradesByToken = groupedTrades[token];

      const sum = sumTradeFields(tradesByToken);

      return (
        <div className="surface-trades" key={token}>
          {renderHeader(token)}
          <div className="trades-rows">{renderTradeRow(tradesByToken || [])}</div>
          <div className="trades-totals">
            {TRADES_PARAMS_LABELS.map((param) => {
              const parent = getParent(param.key);
              const isChild = !!parent;
              const canRenderChild = isChild && expandableColumns.includes(parent.key);
              const canRender = !isChild || canRenderChild;

              if (!canRender) return null;

              if (param.key === TradeParamsEnum.instrument) {
                return (
                  <span className="total" key={param.key}>
                    Total:
                  </span>
                );
              }

              if (param.key === TradeParamsEnum.notional) {
                return <span key={param.key}></span>;
              }

              return <span key={param.key}>{sum[param.key]}</span>;
            })}
          </div>
          {tokenPairToken ? renderTradeButtons(token) : null}
        </div>
      );
    });
  }, [groupedTrades, tkPair, TRADES_PARAMS_LABELS, expandableColumns]);

  const renderResetModal = useMemo(() => {
    return (
      <ConfirmResetModal
        open={openModal}
        handleClose={handleCloseModal}
        handleResetData={() => resetTrade(!!tkPair, resetToken)}
        message={MODAL_RESET_MESSAGE}
        title={MODAL_RESET_TITLE}
        confirmButton={MODAL_CONFIRM_BUTTON}
      />
    );
  }, [openModal, resetTrade, tkPair, handleCloseModal, resetToken]);

  return (
    <div className="surface-trades-container">
      {renderClearTrades}
      {renderTokenTrades}
      {renderResetModal}
    </div>
  );
};

export default SurfaceTrades;
